import React, { Fragment, useRef, useContext } from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SearchIcon,
    CogIcon,
    SupportIcon,
    TrashIcon,
} from "@heroicons/react/outline";
import { Link, useHistory, useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import { EntryListContext } from ".";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { apiDel, messageFromException } from "helpers/Request";
import { useContextualHelp } from "hooks/Help";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function AuthorListToolbar(props) {
    const inputRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const auth = useAuth();
    const contextualHelp = useContextualHelp();

    const { selectedItems, load } = useContext(EntryListContext);

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && val === null) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Entries",
            children: (
                <Fragment>
                    <div>Are you sure you want to delete these entries?</div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={handleDelete}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const id = toast.loading("Deleting entries...");
        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/entry/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                modal.dismiss();
                toast.success("Entries deleted", { id });
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error Deleting Entries",
                    children: messageFromException(err),
                });
            });
    };

    const onKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        onSearchClick();
    };
    const onSearchClick = () => {
        const val = inputRef.current.value;
        if (!val || !val.length) {
            updateParams("keyword", null);
        } else {
            updateParams("keyword", val);
        }
    };

    return (
        <Toolbar>
            <ToolbarGroup>
                <input
                    type="text"
                    ref={inputRef}
                    className="focus:outline-none relative mr-2 flex w-3/4 items-center rounded-md border border-gray-300 bg-white py-0 px-2 text-xs placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 sm:w-auto"
                    placeholder="Search Entries"
                    onKeyDown={onKeyDown}
                />
                <ToolbarButton icon={<SearchIcon />} onClick={onSearchClick} />
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<CogIcon />}
                    as={Link}
                    to="/app/custom-content/channel"
                >
                    <span className="hidden sm:inline">Manage Channels</span>
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={onRequestDelete}
                    disabled={!selectedItems.length}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    className="text-green-600"
                    onClick={() =>
                        contextualHelp.showContextualHelp(
                            "/custom-content/index.html"
                        )
                    }
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
